import { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, Loader2, ExternalLink } from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface SyncRecord {
  id: string;
  entityType: 'invoice' | 'purchase_order' | 'journal' | 'payment';
  entityId: string;
  entityNumber: string;
  tallyStatus: 'pending' | 'synced' | 'failed' | 'partial';
  syncedAt: string | null;
  errorMessage: string | null;
  retryCount: number;
}

export function TallySyncPage() {
  const [records, setRecords] = useState<SyncRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    fetchSyncStatus();
  }, []);

  const fetchSyncStatus = async () => {
    try {
      const res = await api.get('/integrations/tally/sync-status');
      setRecords(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch tally sync status:', err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await api.post('/integrations/tally/sync');
      fetchSyncStatus();
    } catch (err) {
      alert('Sync triggered - check status in a moment');
    } finally {
      setSyncing(false);
    }
  };

  const retryFailed = async (recordId: string) => {
    try {
      await api.post(`/integrations/tally/retry/${recordId}`);
      fetchSyncStatus();
    } catch (err) {
      alert('Failed to retry');
    }
  };

  const filteredRecords = records.filter(r => 
    !filter || r.entityType === filter || r.tallyStatus === filter
  );

  const stats = {
    total: records.length,
    synced: records.filter(r => r.tallyStatus === 'synced').length,
    failed: records.filter(r => r.tallyStatus === 'failed').length,
    pending: records.filter(r => r.tallyStatus === 'pending').length,
    partial: records.filter(r => r.tallyStatus === 'partial').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tally Sync Status</h1>
          <p className="text-sm text-gray-500">Sync status between AURA and Tally Prime</p>
        </div>
        <div className="flex gap-2">
          <button onClick={triggerSync} disabled={syncing} className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">
            {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            Sync Now
          </button>
          <button className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm text-white hover:bg-[var(--ink-1)]">
            <ExternalLink size={16} />
            Open Tally
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-5">
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-500">Total</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{stats.synced}</p>
          <p className="text-sm text-gray-500">Synced</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
          <p className="text-sm text-gray-500">Failed</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
          <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
          <p className="text-sm text-gray-500">Pending</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">{stats.partial}</p>
          <p className="text-sm text-gray-500">Partial</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex gap-3">
        <select value={filter} onChange={e => setFilter(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
          <option value="">All Records</option>
          <option value="invoice">Invoices</option>
          <option value="purchase_order">Purchase Orders</option>
          <option value="journal">Journal Entries</option>
          <option value="payment">Payments</option>
          <option value="synced">Synced</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Records Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left">
              <th className="px-4 py-3 font-medium text-gray-500">Entity</th>
              <th className="px-4 py-3 font-medium text-gray-500">Number</th>
              <th className="px-4 py-3 font-medium text-gray-500">Type</th>
              <th className="px-4 py-3 font-medium text-gray-500">Status</th>
              <th className="px-4 py-3 font-medium text-gray-500">Synced At</th>
              <th className="px-4 py-3 font-medium text-gray-500">Error</th>
              <th className="px-4 py-3 font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredRecords.map(record => (
              <tr key={record.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 capitalize text-gray-900">{record.entityType.replace('_', ' ')}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{record.entityNumber}</td>
                <td className="px-4 py-3 text-gray-600">{record.entityType}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                    record.tallyStatus === 'synced' ? 'bg-green-100 text-green-700' :
                    record.tallyStatus === 'failed' ? 'bg-red-100 text-red-700' :
                    record.tallyStatus === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {record.tallyStatus === 'synced' && <CheckCircle size={12} />}
                    {record.tallyStatus === 'failed' && <XCircle size={12} />}
                    {record.tallyStatus === 'pending' && <Clock size={12} />}
                    {record.tallyStatus === 'partial' && <AlertTriangle size={12} />}
                    {record.tallyStatus}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600">{record.syncedAt ? formatDate(record.syncedAt) : '-'}</td>
                <td className="px-4 py-3 text-red-600 text-xs">{record.errorMessage || '-'}</td>
                <td className="px-4 py-3">
                  {record.tallyStatus === 'failed' && record.retryCount < 3 && (
                    <button onClick={() => retryFailed(record.id)} className="text-xs text-[var(--ink-0)] hover:text-[var(--ink-0)]">
                      Retry
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}